import React from 'react';
import styled from 'styled-components';

import { Typography, Table, Row, Divider, Space } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { formatMoney } from 'helpers/Formatter';

const { Text } = Typography;

const StyledTable = styled(Table)`
  .ant-table-expand-icon-col {
    width: 24px;
  }
  .ant-table-row-expand-icon-cell {
    padding: 4px 0px;
  }
  .ant-table-tbody > tr > td {
    border: none;
  }
  .ant-table-cell {
    padding: 4px 0;
  }
  tr.ant-table-expanded-row > td,
  tr.ant-table-expanded-row:hover > td {
    background: none;
  }
`;

const paymentsColumns = [
  { title: 'Name', dataIndex: 'name', key: 'name', align: 'left' },
  { title: 'Price', dataIndex: 'price', key: 'price', align: 'right' },
];

const SidebarPaymentDetails = ({ leasePricing }) => {
  const monthlyCharges = () => {
    const leaseCharges = (leasePricing?.lease_charges || []).map((charge) => ({
      description: charge.item_type,
      price: formatMoney(charge.rent_amount),
    }));

    return {
      key: 1,
      name: 'Monthly charges',
      price: formatMoney(leasePricing?.total_rent_amount),
      expandableItems: [
        { description: 'Suite rental', price: formatMoney(leasePricing?.rent_amount) },
        ...leaseCharges,
      ],
    };
  };

  const oneTimeCharges = () => {
    const leaseChargesDeposits = (leasePricing?.lease_charges || []).map((charge) => ({
      description: `${charge.item_type} deposit`,
      price: formatMoney(charge.deposit_amount),
    }));
    const leaseChargesKeyDeposits = (leasePricing?.lease_charges || []).map((charge) => ({
      description: `${charge.item_type} key deposit`,
      price: formatMoney(charge.key_deposit_amount),
    }));

    return {
      key: 2,
      name: 'One-time charges',
      price: formatMoney(leasePricing?.total_deposit_amount),
      expandableItems: [
        { description: 'Security deposit', price: formatMoney(leasePricing?.deposit_amount) },
        { description: 'Key deposit', price: formatMoney(leasePricing?.key_deposit_amount) },
        ...leaseChargesDeposits,
        ...leaseChargesKeyDeposits,
      ],
    };
  };

  const paymentsDue = () => {
    const leaseChargesDeposits = (leasePricing?.lease_charges || []).map((charge) => ({
      description: `${charge.item_type} deposit`,
      price: formatMoney(charge.deposit_amount),
    }));
    const leaseChargesKeyDeposits = (leasePricing?.lease_charges || []).map((charge) => ({
      description: `${charge.item_type} key deposit`,
      price: formatMoney(charge.key_deposit_amount),
    }));
    const incentives = leasePricing?.prepayments?.map((prepayment) => ({
      description: prepayment.incentive,
      price: formatMoney(prepayment.amount * -1),
    }));
    const payments = leasePricing?.batch_payments?.map((batch_payment) => ({
      description: `Payment - ${batch_payment.cotenant_full_name}`,
      price: formatMoney(batch_payment.amount * -1),
    }));

    return {
      key: 3,
      name: 'Due at approval',
      price: formatMoney(leasePricing?.deposit_owing),
      expandableItems: [
        { description: 'Security deposit', price: formatMoney(leasePricing?.deposit_amount) },
        { description: 'Key deposit', price: formatMoney(leasePricing?.key_deposit_amount) },
        ...leaseChargesDeposits,
        ...leaseChargesKeyDeposits,
        ...incentives,
        ...payments,
      ],
    };
  };

  const charges = [monthlyCharges(), oneTimeCharges()];

  return (
    <>
      <Divider />
      <Space>
        <DollarOutlined />
        <Text strong>Charges</Text>
      </Space>
      <StyledTable
        showHeader={false}
        pagination={false}
        columns={paymentsColumns}
        expandable={{
          expandedRowRender: (record) =>
            record.expandableItems.map((item) => (
              <Row key={item.description + item.price} justify="space-between">
                <Text type="secondary">{item.description}</Text>
                {item.price && <Text type="secondary">{item.price}</Text>}
              </Row>
            )),
        }}
        dataSource={charges}
      />
      <Divider />
      <Space>
        <DollarOutlined />
        <Text strong>Payments due</Text>
      </Space>
      <StyledTable
        showHeader={false}
        pagination={false}
        columns={paymentsColumns}
        expandable={{
          expandedRowRender: (record) =>
            record.expandableItems.map((item) => (
              <Row key={item.description + item.price} justify="space-between">
                <Text type="secondary">{item.description}</Text>
                {item.price && <Text type="secondary">{item.price}</Text>}
              </Row>
            )),
        }}
        dataSource={[paymentsDue()]}
      />
    </>
  );
};

export default SidebarPaymentDetails;
